import { Position } from '@xyflow/react';
import type { CSSProperties } from 'react';
import type { MapAnchorSide, MapEdgeAnchor, MapNodeRow } from '../../lib/systemMap';

export const DEFAULT_NODE_WIDTH = 190;
export const DEFAULT_NODE_HEIGHT = 72;
export const NODE_HEIGHT_WITH_THUMB = 140;

/** Matches React Flow Background gap in SystemMapCanvas. */
export const SYSTEM_MAP_SNAP_GRID: [number, number] = [20, 20];

export function snapSystemMapPosition(x: number, y: number): { x: number; y: number } {
  const [gridX, gridY] = SYSTEM_MAP_SNAP_GRID;
  return {
    x: Math.round(x / gridX) * gridX,
    y: Math.round(y / gridY) * gridY,
  };
}

export function systemMapNodeSize(
  showThumbnail: boolean,
  width?: number | null,
  height?: number | null,
  kind?: string,
): { width: number; height: number } {
  if (kind === 'automation') {
    const w = width != null && width > 0 ? width : 280;
    const h = height != null && height > 0 ? height : 132;
    return { width: w, height: h };
  }
  if (kind === 'automation_step') {
    const w = width != null && width > 0 ? width : 200;
    const h = height != null && height > 0 ? height : 224;
    return { width: w, height: h };
  }
  const w = width != null && width > 0 ? width : DEFAULT_NODE_WIDTH;
  const h =
    height != null && height > 0 ? height : showThumbnail ? NODE_HEIGHT_WITH_THUMB : DEFAULT_NODE_HEIGHT;
  return { width: w, height: h };
}

export function nodeRowSize(node: MapNodeRow): { width: number; height: number } {
  return systemMapNodeSize(node.show_thumbnail, node.width, node.height, node.kind);
}

export function anchorSideToPosition(side: MapAnchorSide): Position {
  const map: Record<MapAnchorSide, Position> = {
    top: Position.Top,
    right: Position.Right,
    bottom: Position.Bottom,
    left: Position.Left,
  };
  return map[side];
}

export function handleStyleForAnchor(side: MapAnchorSide, offset: number): CSSProperties {
  const pct = `${Math.round(offset * 1000) / 10}%`;
  switch (side) {
    case 'top':
      return { left: pct, top: 0, transform: 'translate(-50%, -50%)' };
    case 'bottom':
      return { left: pct, top: '100%', transform: 'translate(-50%, -50%)' };
    case 'left':
      return { top: pct, left: 0, transform: 'translate(-50%, -50%)' };
    case 'right':
      return { top: pct, left: '100%', transform: 'translate(-50%, -50%)' };
  }
}

export function anchorToFlowPoint(
  nodeX: number,
  nodeY: number,
  width: number,
  height: number,
  anchor: MapEdgeAnchor,
): { x: number; y: number } {
  const offset = Math.max(0, Math.min(1, anchor.offset));
  switch (anchor.side) {
    case 'top':
      return { x: nodeX + width * offset, y: nodeY };
    case 'bottom':
      return { x: nodeX + width * offset, y: nodeY + height };
    case 'left':
      return { x: nodeX, y: nodeY + height * offset };
    case 'right':
      return { x: nodeX + width, y: nodeY + height * offset };
  }
}

export function snapFlowPointToNodeAnchor(
  nodeX: number,
  nodeY: number,
  width: number,
  height: number,
  flowX: number,
  flowY: number,
): MapEdgeAnchor {
  const left = nodeX;
  const right = nodeX + width;
  const top = nodeY;
  const bottom = nodeY + height;

  const topX = Math.max(left, Math.min(right, flowX));
  const bottomX = topX;
  const leftY = Math.max(top, Math.min(bottom, flowY));
  const rightY = leftY;

  const candidates: { side: MapAnchorSide; x: number; y: number; offset: number }[] = [
    { side: 'top', x: topX, y: top, offset: width > 0 ? (topX - left) / width : 0.5 },
    { side: 'bottom', x: bottomX, y: bottom, offset: width > 0 ? (bottomX - left) / width : 0.5 },
    { side: 'left', x: left, y: leftY, offset: height > 0 ? (leftY - top) / height : 0.5 },
    { side: 'right', x: right, y: rightY, offset: height > 0 ? (rightY - top) / height : 0.5 },
  ];

  let best = candidates[0];
  let bestDist = Infinity;
  for (const c of candidates) {
    const d = (c.x - flowX) ** 2 + (c.y - flowY) ** 2;
    if (d < bestDist) {
      bestDist = d;
      best = c;
    }
  }

  return {
    side: best.side,
    offset: Math.max(0.08, Math.min(0.92, best.offset)),
  };
}
